import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import ActionIconButton from '@components/buttons/ActionIconButton';
import styles from '@components/ConsultReportDiscussionItem.module.css';
import DeleteConfirmationAlert from '@components/DeleteConfirmationAlert';
import FocusArea from '@components/FocusArea';
import Nbsp from '@components/Nbsp';
import HugeText from '@components/text/HugeText';
import Hyperlink from '@components/typography/Hyperlink';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import {
  ConsultReportDiscussionItemFragment as ConsultReportDiscussionItemType,
  ConsultFragment as ConsultType
} from '@typing/Generated';
import { journeyRoute } from '@utils/routeUtils';

export enum Context {
  CARE_JOURNEY = 'CARE_JOURNEY',
  CONSULT_REPORT = 'CONSULT_REPORT'
}

type Props = {
  consult: ConsultType | null;
  context: Context;
  discussionItem: ConsultReportDiscussionItemType;
  journeyId: string;
  onDelete?: (discussionItem: ConsultReportDiscussionItemType) => void;
  onEdit?: (discussionItem: ConsultReportDiscussionItemType) => void;
};

const ConsultReportDiscussionItem = ({ consult, context, discussionItem, journeyId, onDelete, onEdit }: Props) => {
  const deleteOpener = useOpener();
  const myExpert = useMyExpert();

  const handleEdit = useCallback(() => {
    onEdit?.(discussionItem);
  }, [discussionItem, onEdit]);

  const handleDelete = useCallback(() => {
    onDelete?.(discussionItem);
  }, [discussionItem, onDelete]);

  const sanitizedNotes = useSanitizeHtml(discussionItem.notes, journeyId, discussionItem);

  const sanitizedNextSteps = useSanitizeHtml(discussionItem.nextSteps, journeyId, discussionItem);

  return (
    <div className={styles.discussionItemWrapper}>
      <div className={styles.discussionItemBody}>
        <IonGrid>
          <IonRow className="ion-align-items-center">
            <IonCol>
              <HugeText>{discussionItem.title}</HugeText>
            </IonCol>
            <IonCol>
              <IonGrid>
                <IonRow className="ion-align-items-center ion-justify-content-end">
                  <IonCol className="ion-text-right">
                    {discussionItem.focusAreas.map(focusArea => (
                      <span className={styles.focusAreaWrapper} key={focusArea}>
                        <FocusArea focusArea={focusArea} />
                      </span>
                    ))}
                  </IonCol>
                  {context === Context.CONSULT_REPORT && (
                    <IonCol size="auto">
                      {onEdit && (
                        <ActionIconButton
                          ariaLabelKey="accessibility.ariaLabels.edit"
                          icon="edit"
                          onClick={handleEdit}
                        />
                      )}
                      {onDelete && (
                        <ActionIconButton
                          ariaLabelKey="accessibility.ariaLabels.delete"
                          icon="trash"
                          onClick={deleteOpener.open}
                        />
                      )}
                    </IonCol>
                  )}
                </IonRow>
              </IonGrid>
            </IonCol>
          </IonRow>
          {consult && context === Context.CARE_JOURNEY && (
            <IonRow className={styles.consultReportLink}>
              <IonCol>
                <>
                  <FormattedMessage id="models.consultReportDiscussionItem.discussedDuringConsult" />
                  <Nbsp />
                  <Hyperlink
                    routerLink={journeyRoute({
                      journeyId,
                      linkAfterJourney: `/consults/${consult.id}/consult-report`,
                      myExpert
                    })}
                  >
                    <FormattedMessage id="models.consult.genericName" />
                  </Hyperlink>
                </>
              </IonCol>
            </IonRow>
          )}

          <IonRow>
            <IonCol>
              <div className={styles.dataSection}>
                <div>
                  <strong>
                    <FormattedMessage id="models.consultReportDiscussionItem.notes" />
                  </strong>
                </div>
                <div>{sanitizedNotes}</div>
              </div>

              <div className={styles.dataSection}>
                <div>
                  <strong>
                    <FormattedMessage id="models.consultReportDiscussionItem.nextSteps" />
                  </strong>
                </div>
                <div>{sanitizedNextSteps}</div>
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
      <DeleteConfirmationAlert
        messageKey="models.consultReportDiscussionItem.reallyDelete"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
    </div>
  );
};

export default ConsultReportDiscussionItem;
